import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { teamRepository } from '../repositories/team.repository';
import { orgKeys } from '../services/organization.keys';
import { useOrganization } from './useOrganization';
import type { UserManagementUser, AppRole } from '../types/organization.types';

export type { UserManagementUser };

export function useUserManagement() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgKeys.userManagement(orgId || ''),
    queryFn: async (): Promise<UserManagementUser[]> => {
      if (!orgId) return [];
      return teamRepository.getOrgUserView(orgId);
    },
    enabled: !!orgId,
  });
}

type UpdateUserRoleParam =
  | { userId: string; newRole: AppRole }
  | { userOrgId: string; role: AppRole };

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async (param: UpdateUserRoleParam) => {
      if ('userId' in param) {
        if (!orgId) throw new Error('No organization selected');
        await teamRepository.updateUserRoleInOrg(orgId, param.userId, param.newRole);
      } else {
        await teamRepository.updateMemberRole(param.userOrgId, param.role);
      }
    },
    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: orgKeys.userManagement(orgId) });
        queryClient.invalidateQueries({ queryKey: orgKeys.teamMembers(orgId) });
        queryClient.invalidateQueries({ queryKey: orgKeys.userRole(orgId) });
      }
      queryClient.invalidateQueries({ queryKey: orgKeys.legacyTeamRoles() });
      queryClient.invalidateQueries({ queryKey: ['user-management'] });
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
      queryClient.invalidateQueries({ queryKey: ['user-organizations'] });
      toast({
        title: 'Success',
        description: 'User role updated successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error updating user role:', error);
      toast({
        title: 'Error updating role',
        description: error?.message || 'Failed to update user role',
        variant: 'destructive',
      });
    },
  });
}

export function useToggleUserStatus() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async ({ userId, newStatus }: { userId: string; newStatus: 'active' | 'suspended' }) => {
      if (!orgId) throw new Error('No organization selected');
      await teamRepository.toggleUserStatus(orgId, userId, newStatus);
    },
    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: orgKeys.userManagement(orgId) });
        queryClient.invalidateQueries({ queryKey: orgKeys.teamMembers(orgId) });
      }
      queryClient.invalidateQueries({ queryKey: ['user-management'] });
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
      queryClient.invalidateQueries({ queryKey: ['user-organizations'] });
      toast({
        title: 'User status updated',
        description: "The user's status has been changed.",
      });
    },
    onError: (error: any) => {
      console.error('Error toggling user status:', error);
      toast({
        title: 'Error updating status',
        description: error?.message || 'Failed to update status',
        variant: 'destructive',
      });
    },
  });
}

export function useRemoveUser() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async (userOrgId: string) => {
      await teamRepository.removeMember(userOrgId);
    },
    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: orgKeys.userManagement(orgId) });
        queryClient.invalidateQueries({ queryKey: orgKeys.teamMembers(orgId) });
      }
      queryClient.invalidateQueries({ queryKey: orgKeys.legacyTeamRoles() });
      toast({
        title: 'Success',
        description: 'User removed successfully',
      });
    },
    onError: (error: any) => {
      console.error('Error removing user:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to remove user',
        variant: 'destructive',
      });
    },
  });
}
